import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowDown,
  ArrowUpRight,
  FolderKanban,
  Megaphone,
  Users,
  BookOpen,
  PenLine,
  UserPlus,
  CalendarDays,
  Compass,
  Landmark,
  Handshake,
} from "lucide-react";
import { Reveal, SectionHead, Counter, BannerStrip, Tag, PhotoFrame } from "@/components/Shared";
import ProjectCard from "@/components/ProjectCard";
import { useCollection, usePageContent, fmtDate, IMG } from "@/lib/api";
import { PAGES } from "@/content/pageContent";

const EASE = [0.22, 1, 0.36, 1];

const QUICK_LINKS = [
  { to: "/projects", icon: FolderKanban, title: "Our Projects", note: "Discover the initiatives we're working on." },
  { to: "/what-are-we-up-to", icon: Megaphone, title: "What's Happening", note: "See what we're currently up to." },
  { to: "/team", icon: Users, title: "Our Team", note: "Meet the 2026–27 Board of Directors." },
  { to: "/publications", icon: BookOpen, title: "Publications", note: "Explore our magazines and newsletters." },
  { to: "/blog", icon: PenLine, title: "Our Blog", note: "Read stories, opinions and experiences from our members." },
  { to: "/contact", icon: UserPlus, title: "Join / Connect With Us", note: "Find out how to get involved." },
  { to: "/our-north-star", icon: Compass, title: "Our North Star", note: "The vision that guides everything we do." },
  { to: "/rotary-club-of-bombay-central", icon: Landmark, title: "Rotary Club of Bombay Central", note: "Our sponsor club and guiding force, RID 3141." },
  { to: "/collaborators", icon: Handshake, title: "Collaborators & Sponsors", note: "The partners who stand behind our work." },
];

const ordinal = (n) => {
  if (!n || isNaN(n)) return "—";
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

function Hero({ introDone }) {
  const c = usePageContent("home", PAGES.home.defaults);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const lines = [
    { cls: "font-grotesk text-white text-4xl sm:text-6xl lg:text-[4.75rem] font-semibold leading-[1.05] tracking-tight", text: c.heroLine1 },
    { cls: "font-grotesk italic text-white/90 text-4xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] tracking-tight", text: c.heroLine2 },
  ];

  return (
    <section ref={ref} className="relative flex min-h-screen items-end overflow-hidden" data-testid="home-hero">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={IMG.heroLeopard} alt="A snow leopard in a midnight-blue forest" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/30 to-ink" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-28 pt-48">
        {lines.map((l, i) => (
          <div key={i} className="overflow-hidden pb-2 sm:pb-3">
            <motion.p
              className={l.cls}
              initial={{ y: "115%" }}
              animate={introDone ? { y: 0 } : { y: "115%" }}
              transition={{ duration: 1.1, delay: 0.1 + i * 0.15, ease: EASE }}
              data-testid="hero-line"
            >
              {l.text}
            </motion.p>
          </div>
        ))}
        <motion.p
          className="mt-8 max-w-xl text-sm leading-relaxed text-silver-dim sm:text-base"
          initial={{ opacity: 0, y: 24 }}
          animate={introDone ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.65, ease: EASE }}
          data-testid="hero-welcome"
        >
          {c.heroWelcome}
        </motion.p>
        <motion.div
          className="mt-10 flex flex-wrap gap-4"
          initial={{ opacity: 0, y: 24 }}
          animate={introDone ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
        >
          <Link to="/about" className="btn-ember" data-testid="hero-explore-journey-btn">
            <span>Explore Our Journey</span> <ArrowDown size={15} />
          </Link>
          <Link to="/team" className="btn-silver" data-testid="hero-meet-team-btn">
            <span>Meet the Team</span>
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-8 right-8 z-10 hidden flex-col items-center gap-3 text-silver-muted md:flex"
        initial={{ opacity: 0 }}
        animate={introDone ? { opacity: 1 } : {}}
        transition={{ delay: 1.4, duration: 0.8 }}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ writingMode: "vertical-rl" }}>
          Scroll
        </span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}

function PresidentsNote() {
  const c = usePageContent("home", PAGES.home.defaults);
  const [expanded, setExpanded] = useState(false);
  const { items: teamItems } = useCollection("team");
  const president = (teamItems || []).find((m) => m.position === "President");
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 sm:py-32" data-testid="presidents-note">
      <div className="grid items-start gap-14 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="relative">
          <PhotoFrame
            src={president?.photo || null}
            alt={c.presidentName}
            label="President's photograph — add via Admin"
            className="aspect-[3/4] w-full max-w-md mx-auto bg-navy"
          />
        </div>
        <div>
          <SectionHead title="From the President's Heart" />
          <Reveal delay={0.2}>
            <div className="mt-8 space-y-5">
              {c.presidentOpening.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-silver-dim">{p}</p>
              ))}
            </div>
          </Reveal>
          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="overflow-hidden"
                data-testid="president-note-expanded"
              >
                <div className="space-y-5 pt-5">
                  {c.presidentRest.map((p, i) => (
                    <p key={i} className="text-base leading-relaxed text-silver-dim">{p}</p>
                  ))}
                  <div className="pt-4">
                    <p className="font-display text-xl italic text-silver">{c.presidentName}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">
                      {c.presidentRoleLine}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <Reveal delay={0.34}>
            <button onClick={() => setExpanded(!expanded)} className="btn-silver mt-8" data-testid="president-read-more-btn">
              <span>{expanded ? "Read Less" : "Read More"}</span>
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WhoWeAre() {
  const c = usePageContent("home", PAGES.home.defaults);
  const stats = [
    { value: 150, suffix: "+", label: "Active Members" },
    { value: "1st February 2017", label: "Chartered On", raw: true, long: true },
    { value: 10, suffix: "", label: "Areas of Work" },
    { value: 11, suffix: "", label: "Years of Impact" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 sm:py-32" data-testid="who-we-are">
      <div className="grid gap-14 lg:grid-cols-2">
        <div>
          <SectionHead eyebrow="Who We Are" title="A student-led movement of" italic="service & leadership" />
          <Reveal delay={0.2}>
            <div className="mt-8 space-y-5">
              {c.whoWeAre.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed text-silver-dim sm:text-base">{p}</p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <Link to="/about" className="btn-silver mt-10" data-testid="who-we-are-story-btn">
              <span>Know Our Story</span>
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 gap-4 self-center" data-testid="stats-band">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="card-line corner-frame p-8">
                <p className={`font-display font-semibold metallic ${s.long ? "text-2xl sm:text-3xl leading-snug" : "text-4xl sm:text-5xl"}`}>
                  {s.raw ? s.value : <Counter value={s.value} />}
                  {s.raw ? "" : s.suffix}
                </p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-silver-muted">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function EventColumn({ title, events, tone }) {
  return (
    <div data-testid={`event-column-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <Reveal>
        <p className="eyebrow flex items-center gap-3">
          <span className={`inline-block h-2 w-2 rotate-45 ${tone === "ember" ? "bg-ember" : "bg-silver-dim"}`} />
          {title}
        </p>
      </Reveal>
      <div className="mt-6 space-y-4">
        {events.slice(0, 2).map((e, i) => (
          <Reveal key={e.id} delay={i * 0.1}>
            <div className="card-line flex gap-5 p-6" data-testid="event-mini-card">
              <div className="corner-frame flex h-16 w-16 shrink-0 flex-col items-center justify-center border border-silver/20 bg-ink/50">
                <span className="font-display text-2xl font-semibold text-silver">{ordinal(new Date(e.date).getDate())}</span>
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-silver-muted">
                  {new Date(e.date).toLocaleString("en-IN", { month: "short" })}
                </span>
              </div>
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-ember">{e.category}</p>
                <h4 className="mt-1 font-display text-xl text-silver">{e.title}</h4>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-silver-muted">{e.description}</p>
              </div>
            </div>
          </Reveal>
        ))}
        {events.length === 0 && (
          <p className="text-sm italic text-silver-muted">New updates will be announced soon.</p>
        )}
      </div>
    </div>
  );
}

export default function Home({ introDone }) {
  const projects = useCollection("projects");
  const events = useCollection("events");
  const blog = useCollection("blog");
  const publications = useCollection("publications");

  const upcoming = (events.items || []).filter((e) => e.status === "upcoming");
  const completed = (events.items || []).filter((e) => e.status !== "upcoming");
  const featured = (projects.items || []).slice(0, 3);
  const latestPost = (blog.items || [])[0];
  const magazine = (publications.items || []).find((p) => p.kind === "magazine") || (publications.items || [])[0];

  return (
    <main>
      <Hero introDone={introDone} />

      <PresidentsNote />
      <WhoWeAre />

      {/* Featured Projects */}
      <section className="mx-auto max-w-7xl px-6 py-24 sm:py-32" data-testid="featured-projects">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead eyebrow="Featured Work" title="Projects that" italic="shaped us" />
          <Reveal>
            <Link to="/projects" className="btn-silver" data-testid="featured-projects-viewall-btn">
              <span>View All Projects</span>
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className="h-full">
              <ProjectCard p={p} index={i} />
            </Reveal>
          ))}
          {projects.loading && <p className="text-sm text-silver-muted">Loading projects…</p>}
        </div>
      </section>

      {/* What are we up to */}
      <BannerStrip image={IMG.celestialSky} height="py-20" />
      <section className="mx-auto max-w-7xl px-6 py-24 sm:py-32" data-testid="whats-up-preview">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead eyebrow="Live Updates" title="What are we" italic="up to?" />
          <Reveal>
            <Link to="/what-are-we-up-to" className="btn-silver" data-testid="whats-up-viewall-btn">
              <span>See Everything</span>
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-12 lg:grid-cols-2">
          <EventColumn title="Upcoming" events={upcoming} tone="ember" />
          <EventColumn title="Recently Completed" events={completed} tone="silver" />
        </div>
      </section>

      {/* Latest blog + publication */}
      <section className="mx-auto max-w-7xl px-6 py-24 sm:py-32" data-testid="latest-editorial">
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal className="h-full">
            <div className="card-line flex h-full flex-col p-8" data-testid="latest-blog-card">
              <div className="flex items-center justify-between">
                <Tag>From the Blog</Tag>
                <Link to="/blog" className="nav-link" data-testid="latest-blog-all-btn">All Posts</Link>
              </div>
              {latestPost ? (
                <>
                  <h3 className="mt-8 font-display text-3xl font-semibold text-silver">{latestPost.title}</h3>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-silver-muted">{latestPost.excerpt}</p>
                  <div className="mt-8 flex items-center justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">
                      {latestPost.author} · {fmtDate(latestPost.date)}
                    </p>
                    <Link to={`/blog/${latestPost.id}`} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-silver-dim hover:text-white" data-testid="latest-blog-read-btn">
                      Read More <ArrowUpRight size={14} className="text-ember" />
                    </Link>
                  </div>
                </>
              ) : (
                <p className="mt-8 flex-1 text-sm italic text-silver-muted">First story arriving soon.</p>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.12} className="h-full">
            <div className="card-line flex h-full flex-col p-8" data-testid="latest-publication-card">
              <div className="flex items-center justify-between">
                <Tag tone="ember">Latest Publication</Tag>
                <Link to="/publications" className="nav-link" data-testid="latest-pub-all-btn">All Publications</Link>
              </div>
              <div className="mt-8 flex flex-1 gap-6">
                <PhotoFrame src={magazine?.cover_image} label="Magazine cover" className="aspect-[3/4] w-32 shrink-0 bg-navy sm:w-40" />
                <div>
                  <h3 className="font-display text-3xl font-semibold metallic">{magazine?.title || "Elysian — In Print, In Spirit"}</h3>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">{magazine?.period || "Q1 | 2026–27"}</p>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-silver-muted">{magazine?.description || "The annual magazine of RCSCW."}</p>
                </div>
              </div>
              <Link to="/publications" className="btn-silver mt-8 self-start" data-testid="latest-pub-read-btn">
                <span>Read Magazine</span>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Upcoming calendar events */}
      <section className="mx-auto max-w-7xl px-6 pb-24 sm:pb-32" data-testid="upcoming-calendar">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead eyebrow="Mark Your Dates" title="Upcoming" italic="calendar events" />
          <Reveal>
            <Link to="/calendar" className="btn-silver" data-testid="calendar-viewall-btn">
              <span>View Full Calendar</span>
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 divide-y divide-silver/10 border-y border-silver/10">
          {upcoming.slice(0, 3).map((e, i) => (
            <Reveal key={e.id} delay={i * 0.08}>
              <div className="group flex flex-wrap items-center gap-6 py-6" data-testid="calendar-row">
                <span className="font-mono text-xs tracking-[0.2em] text-ember">{fmtDate(e.date)}</span>
                <span className="flex-1 font-display text-2xl text-silver transition-colors group-hover:text-white">{e.title}</span>
                <Tag>{e.category}</Tag>
                <CalendarDays size={16} className="text-silver-muted" />
              </div>
            </Reveal>
          ))}
          {!events.loading && upcoming.length === 0 && (
            <p className="py-8 text-sm italic text-silver-muted">The calendar is being updated — check back soon.</p>
          )}
        </div>
      </section>

      {/* Quick links */}
      <section className="mx-auto max-w-7xl px-6 pb-28" data-testid="quick-links">
        <SectionHead eyebrow="Explore RCSCW" title="Quick" italic="links" align="center" />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {QUICK_LINKS.map((q, i) => (
            <Reveal key={q.to} delay={i * 0.07} className="h-full">
              <Link to={q.to} className="card-line group flex h-full flex-col p-8" data-testid={`quick-link-${q.title.toLowerCase().replace(/[\s/]+/g, "-")}`}>
                <q.icon size={26} strokeWidth={1.4} className="text-silver-dim transition-colors duration-300 group-hover:text-ember" />
                <h3 className="mt-6 font-display text-2xl font-semibold text-silver">{q.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-silver-muted">{q.note}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-silver-dim transition-all duration-300 group-hover:gap-3.5 group-hover:text-white">
                  Explore <ArrowUpRight size={13} className="text-ember" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
